import { ExternalLink } from "lucide-react";
import { Marked, Asterisk, HandTag } from "@/components/campaign/HandDrawn";
import { NEWS } from "@/data/content";

const fmt = (iso) =>
  new Date(iso + "T12:00:00").toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const News = () => {
  return (
    <main data-testid="news-page">
      <section className="section border-b border-ink/25">
        <div className="container-x grid gap-12 md:grid-cols-12 items-end">
          <div className="md:col-span-8">
            <p className="overline inline-flex items-center gap-2">
              <Asterisk className="h-4 w-4" /> News &amp; updates
            </p>
            <h1 className="mt-4 font-editorial font-semibold text-5xl md:text-7xl tracking-tight leading-[0.92] text-ink">
              The campaign trail, <br />
              <Marked>in writing</Marked>.
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-ink/75 leading-relaxed text-lg">
              Announcements, essays, and op-eds from Rob.{" "}
              <span className="italic font-semibold text-terracotta text-base">No spin</span> — just the work.
            </p>
          </div>
        </div>
      </section>

      <section className="section bg-parchment" data-testid="news-list-section">
        <div className="container-x space-y-10">
          {NEWS.length === 0 && (
            <p className="text-ink/60 italic">More to come — check back soon.</p>
          )}
          {NEWS.map((n, idx) => (
            <article
              key={n.title}
              className="relative bg-white/5 border border-rule p-8 md:p-12"
              data-testid={`news-item-${idx}`}
            >
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                <HandTag>{n.tag}</HandTag>
                <p className="font-mono text-xs text-ink/65 font-bold">{fmt(n.date)}</p>
              </div>

              <h2 className="mt-6 font-display font-black uppercase text-3xl md:text-5xl text-ink leading-[0.95] tracking-tight max-w-4xl">
                {n.title}
              </h2>

              {n.excerpt && (
                <p className="mt-6 text-xl text-ink/80 italic max-w-3xl border-l-4 border-terracotta pl-5">
                  {n.excerpt}
                </p>
              )}

              {Array.isArray(n.body) && n.body.length > 0 && (
                <div className="mt-8 space-y-5 text-lg leading-relaxed text-ink/85 max-w-3xl">
                  {n.body.map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
              )}

              {n.sourceUrl && (
                <a
                  href={n.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-8 inline-flex items-center gap-2 text-ink font-bold uppercase tracking-wide hover:text-terracotta"
                  data-testid={`news-source-${idx}`}
                >
                  {n.sourceLabel || "Read original source"} <ExternalLink size={16} />
                </a>
              )}

              {Array.isArray(n.resources) && n.resources.length > 0 && (
                <div
                  className="mt-12 border-t-2 border-ink/15 pt-8"
                  data-testid={`news-resources-${idx}`}
                >
                  <p className="overline">If you&apos;re struggling — help is available</p>
                  <p className="mt-3 text-ink/75 text-sm leading-relaxed max-w-2xl">
                    You don&apos;t have to wait for a breaking point. These services are free,
                    confidential, and available 24/7.
                  </p>
                  <ul className="mt-6 grid gap-4 md:grid-cols-3">
                    {n.resources.map((res) => (
                      <li key={res.label} className="border border-rule bg-parchment p-4">
                        <p className="font-bold uppercase tracking-wide text-sm text-ink">
                          {res.label}
                        </p>
                        <a
                          href={res.href}
                          target={res.href?.startsWith("http") ? "_blank" : undefined}
                          rel="noreferrer"
                          className="mt-1 inline-block text-terracotta font-display font-black text-lg hover:underline"
                        >
                          {res.value}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </article>
          ))}
        </div>
      </section>
    </main>
  );
};

export default News;
